import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Play,
  Loader2,
  AlertCircle,
  Info,
  Monitor,
  Tablet,
  Smartphone
} from 'lucide-react'
import { normalizeUrl } from '../utils/urlNormalizer'

const scanSchema = z.object({
  url: z.string().min(1, 'Please enter a URL'),
  wcagLevel: z.enum(['A', 'AA', 'AAA']),
  includeWarnings: z.boolean(),
  viewport: z.object({
    width: z.number().min(320).max(3840),
    height: z.number().min(240).max(2160),
  }),
})

export type ScanFormData = z.infer<typeof scanSchema>

// Viewport presets
const VIEWPORT_PRESETS = {
  desktop: { width: 1920, height: 1080, label: 'Desktop', icon: Monitor },
  tablet: { width: 768, height: 1024, label: 'Tablet', icon: Tablet },
  mobile: { width: 390, height: 844, label: 'Mobile', icon: Smartphone },
}

interface ScanFormProps {
  onSubmit: (data: ScanFormData) => void
  isLoading?: boolean
  compact?: boolean
}

export default function ScanForm({ onSubmit, isLoading = false, compact = false }: ScanFormProps) {
  const [normalizedUrl, setNormalizedUrl] = useState<string>('')

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<ScanFormData>({
    resolver: zodResolver(scanSchema),
    defaultValues: {
      url: '',
      wcagLevel: 'AA',
      includeWarnings: true,
      viewport: {
        width: 1920,
        height: 1080,
      },
    },
  })

  const handleFormSubmit = (data: ScanFormData) => {
    console.log('ScanForm handleFormSubmit called with:', data)
    // Normalize the URL before submitting
    const normalized = normalizeUrl(data.url)
    const submitData = { ...data, url: normalized }
    console.log('Calling onSubmit with normalized data:', submitData)
    onSubmit(submitData)
  }

  // Update normalized URL preview when user types
  const urlValue = watch('url')
  useEffect(() => {
    if (urlValue) {
      setNormalizedUrl(normalizeUrl(urlValue))
    } else {
      setNormalizedUrl('')
    }
  }, [urlValue])

  // Set viewport preset
  const setViewportPreset = (preset: keyof typeof VIEWPORT_PRESETS) => {
    const { width, height } = VIEWPORT_PRESETS[preset]
    setValue('viewport.width', width)
    setValue('viewport.height', height)
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className={compact ? 'space-y-4' : 'space-y-6'}>
        {/* URL Input */}
        <div>
          <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-2">
            Page URL
          </label>
          <input
            {...register('url')}
            type="text"
            placeholder="e.g. heise.de or https://example.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading}
          />
          {normalizedUrl && urlValue && normalizedUrl !== urlValue && (
            <p className="mt-2 text-sm text-gray-600 flex items-center">
              <Info className="w-4 h-4 mr-1" />
              Will test: <span className="font-mono ml-1">{normalizedUrl}</span>
            </p>
          )}
          {errors.url && (
            <p className="mt-2 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.url.message}
            </p>
          )}
        </div>

        {!compact && (
          <>
            {/* WCAG Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                WCAG Compliance Level
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(['A', 'AA', 'AAA'] as const).map((level) => (
                  <label
                    key={level}
                    className={`
                      relative flex items-center justify-center p-3 border rounded-lg cursor-pointer transition-colors
                      ${watch('wcagLevel') === level
                        ? 'border-blue-600 bg-blue-100 text-blue-900'
                        : 'border-gray-300 hover:bg-gray-50'
                      }
                    `}
                  >
                    <input
                      {...register('wcagLevel')}
                      type="radio"
                      value={level}
                      className="sr-only"
                      disabled={isLoading}
                    />
                    <span className="font-medium">Level {level}</span>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Level AA is recommended for most websites
              </p>
            </div>

            {/* Viewport Settings */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Viewport Size
              </label>

              {/* Viewport Presets */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                {Object.entries(VIEWPORT_PRESETS).map(([key, preset]) => {
                  const Icon = preset.icon
                  const isActive = watch('viewport.width') === preset.width &&
                                  watch('viewport.height') === preset.height
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setViewportPreset(key as keyof typeof VIEWPORT_PRESETS)}
                      disabled={isLoading}
                      className={`
                        flex items-center justify-center gap-2 px-3 py-2 border rounded-lg transition-colors
                        ${isActive
                          ? 'border-blue-600 bg-blue-100 text-blue-900'
                          : 'border-gray-300 hover:bg-gray-50'
                        }
                        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-medium">{preset.label}</span>
                    </button>
                  )
                })}
              </div>

              {/* Custom Size Inputs */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="viewport-width" className="block text-xs text-gray-600 mb-1">
                    Width (px)
                  </label>
                  <input
                    {...register('viewport.width', { valueAsNumber: true })}
                    id="viewport-width"
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label htmlFor="viewport-height" className="block text-xs text-gray-600 mb-1">
                    Height (px)
                  </label>
                  <input
                    {...register('viewport.height', { valueAsNumber: true })}
                    id="viewport-height"
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            {/* Include Warnings */}
            <div className="flex items-center">
              <input
                {...register('includeWarnings')}
                type="checkbox"
                id="includeWarnings"
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                disabled={isLoading}
              />
              <label htmlFor="includeWarnings" className="ml-2 text-sm text-gray-700">
                Include warnings in the report (not just errors)
              </label>
            </div>
          </>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className={`
            w-full flex items-center justify-center px-6 py-3 rounded-lg font-medium transition-colors
            ${isLoading
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
            }
          `}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Starting Scan...
            </>
          ) : (
            <>
              <Play className="w-5 h-5 mr-2" />
              Start Accessibility Test
            </>
          )}
        </button>
      </div>
    </form>
  )
}
